import { Schema } from 'effect';
import { OPIN_PARTICIPANT_ROLES, OpinParticipantRole } from './participant-role.ts';
import { dual } from 'effect/Function';

/**
 * One stated decision per OPIN participant role.
 *
 * Before this record, `role === 'receiver'` (and its `!== 'transmitter'` twin) stood in for
 * every regulated shape a deployment takes, restated at roughly thirty call sites across six
 * packages — including two hand-written copies of the same BRCAC posture predicates, in
 * `infra/pulumi/runtime/src/core/opin/participant-role.ts` and
 * `infra/pulumi/runtime-cd-runner/src/deploy/preflight/brcac-cert/posture.ts`. Every site
 * re-decided the rule, so admitting a fourth role would have meant finding and re-deciding all
 * of them, and the two posture copies could drift apart without anything noticing.
 *
 * Axes are named for the decision, never for the role that happens to make it: a site asks
 * "does this deployment serve the provider surface?", not "is the role a transmitter?".
 *
 * This mirrors `environment-policy.ts` (`DeploymentEnvironmentPolicy`) and
 * `provider-support.ts` (`RuntimeCloud`), which already do exactly this for the deployment
 * environment and cloud vocabularies.
 */

/**
 * Whether a deployment stands up one side of the regulated OPIN data exchange.
 *
 * Not a presence/absence rename: the two surfaces are independent axes with different
 * regulated content, and the same word on each answers a different question.
 */
export const OPIN_DATA_SURFACES = ['served', 'absent'] as const;
export const OpinDataSurface = Schema.Literals(OPIN_DATA_SURFACES);
export type OpinDataSurface = typeof OpinDataSurface.Type;

/** Whether Open Finance Brasil participation is on when the operator states nothing. */
export const OPIN_OFB_PARTICIPATION_DEFAULTS = ['enabled', 'disabled'] as const;
export const OpinOfbParticipationDefault = Schema.Literals(OPIN_OFB_PARTICIPATION_DEFAULTS);
export type OpinOfbParticipationDefault = typeof OpinOfbParticipationDefault.Type;

/** Strict contract: decode it with `{ onExcessProperty: 'error' }`. */
export const OpinParticipantRolePolicy = Schema.Struct({
  /** The role this policy governs. Must equal its key in the catalog. */
  role: OpinParticipantRole,

  /**
   * Whether the deployment stands up the regulated PROVIDER surface: the OPIN/OFB provider
   * APIs and their scope guards, the BRCAC mTLS listener and its certificate pair, the
   * provider-DCR gateway client plus its initial-access-token secret, the Keycloak
   * request-object encryption key, the Directory issuer, and the mTLS probe client identity
   * the deploy smoke uses. These are facets of one decision, not separate axes — a
   * receiver-only deployment mounts none of them, and the code comments at each of those
   * sites (issue #617 F26/F27) already said they track the same signal.
   */
  providedDataSurface: OpinDataSurface,

  /**
   * Whether the deployment stands up the regulated CONSUMER surface: the receiver control
   * and callback routes, the receiver JWKS/JWS/JWE and state/data-encryption material, the
   * receiver mTLS client pair, the participant egress FQDN allow-list, the Directory
   * identity coordinates (organisation, software statement, Directory client), and the
   * conformance receiver mode.
   */
  consumedDataSurface: OpinDataSurface,

  /**
   * Whether Open Finance Brasil participation is on when the operator sets no
   * `ofbEnabled`.
   *
   * Its values track {@link providedDataSurface} today, and it is stated as its own axis for
   * the reason `infra/keycloak/src/realms/finnest-ofb.ts` records under issue #617 F26: OFB
   * has no independent wired provider-DCR signal yet, so it deliberately reuses the OPIN
   * role signal, and issue #560 is expected to give it one. When that lands, this axis
   * changes and the provider surface does not.
   */
  defaultOfbParticipation: OpinOfbParticipationDefault,

  /** Operator-facing name, used by the setup wizard and operator-configuration output. */
  displayName: Schema.NonEmptyString,

  /** One line telling an operator what choosing this role commits the deployment to. */
  operatorHint: Schema.NonEmptyString,
}).annotate({
  identifier: 'runtime-cd.opin.participant-role-policy',
  title: 'OpinParticipantRolePolicy',
  description: 'Every decision an OPIN participant role makes, stated once per role.',
});
export type OpinParticipantRolePolicy = typeof OpinParticipantRolePolicy.Type;

/**
 * One policy per member of the role vocabulary, keyed by that role. Keyed by the vocabulary
 * schema itself, so a role added to `@finnest/regulatory-vocabulary` is a required key here.
 * Strict contract: decode it with `{ onExcessProperty: 'error' }`.
 */
export const OpinParticipantRolePolicyCatalog = Schema.Record(
  OpinParticipantRole,
  OpinParticipantRolePolicy,
).check(
  Schema.makeFilter(
    (catalog: Readonly<Record<OpinParticipantRole, OpinParticipantRolePolicy>>) =>
      OPIN_PARTICIPANT_ROLES.every((role) => catalog[role].role === role),
    { message: 'participant role policy catalog keys must match their policy role' },
  ),
);
export type OpinParticipantRolePolicyCatalog = typeof OpinParticipantRolePolicyCatalog.Type;

/**
 * Every value here is what the corresponding call site resolved to before the record existed,
 * verbatim. Introducing it changed no behaviour. Validated once, at module load.
 */
export const OPIN_PARTICIPANT_ROLE_POLICIES = OpinParticipantRolePolicyCatalog.make(
  {
    receiver: {
      role: 'receiver',
      providedDataSurface: 'absent',
      consumedDataSurface: 'served',
      defaultOfbParticipation: 'disabled',
      displayName: 'Receiver',
      operatorHint: 'Receives consented data and does not publish provider APIs.',
    },
    transmitter: {
      role: 'transmitter',
      providedDataSurface: 'served',
      consumedDataSurface: 'absent',
      defaultOfbParticipation: 'enabled',
      displayName: 'Transmitter',
      operatorHint: 'Publishes provider APIs and serves consented data.',
    },
    both: {
      role: 'both',
      providedDataSurface: 'served',
      consumedDataSurface: 'served',
      defaultOfbParticipation: 'enabled',
      displayName: 'Both',
      operatorHint: 'Receives data and publishes provider APIs.',
    },
  } satisfies Record<OpinParticipantRole, OpinParticipantRolePolicy>,
  { parseOptions: { onExcessProperty: 'error' } },
);

/** The total lookup. No Result, no optionality: every role has a complete policy. */
export function opinParticipantRolePolicy(role: OpinParticipantRole): OpinParticipantRolePolicy {
  return OPIN_PARTICIPANT_ROLE_POLICIES[role];
}

/** Whether this role's deployment stands up the regulated provider surface. */
export function opinProvidesData(role: OpinParticipantRole): boolean {
  return opinParticipantRolePolicy(role).providedDataSurface === 'served';
}

/** Whether this role's deployment stands up the regulated data-consumption surface. */
export function opinConsumesData(role: OpinParticipantRole): boolean {
  return opinParticipantRolePolicy(role).consumedDataSurface === 'served';
}

/**
 * A mapped generic is intentional here: the schema owns the policy data, while this callable
 * capability makes TypeScript reject an incomplete role dispatch table before the program can
 * run.
 */
export type OpinParticipantRoleRegistry<Value> = Readonly<Record<OpinParticipantRole, Value>>;

export function opinParticipantRoleRegistry<Value>(
  registry: OpinParticipantRoleRegistry<Value>,
): OpinParticipantRoleRegistry<Value> {
  return Object.freeze(registry);
}

export const selectOpinParticipantRole: {
  (role: OpinParticipantRole): <Value>(registry: OpinParticipantRoleRegistry<Value>) => Value;
  <Value>(registry: OpinParticipantRoleRegistry<Value>, role: OpinParticipantRole): Value;
} = /* @__PURE__ */ dual(
  2,
  <Value>(registry: OpinParticipantRoleRegistry<Value>, role: OpinParticipantRole): Value =>
    registry[role],
);

/**
 * Terminates an exhaustive role dispatch. If the vocabulary gains a member, every caller stays
 * a compile error until it handles the new branch instead of falling through silently.
 */
export function opinParticipantRoleExhausted(role: never): never {
  return role;
}
